import type { TextFacetValue } from "@apptus/esales-api";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useRouter, useSearchParams } from "next/navigation";
import { type ChangeEvent, useRef } from "react";
import { toFilterParam } from "../const";
import removeUnwantedQueryParams from "../services/removeUnwantedQueryParams";

export default function VirtualizedList({
  name,
  values,
}: {
  name: string;
  values: TextFacetValue[];
}) {
  const facetName = toFilterParam("f." + name);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const searchParams = useSearchParams();

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const params = new URLSearchParams(searchParams);

    if (params.has(facetName, event.target.value)) {
      params.delete(facetName, event.target.value);
    } else {
      params.append(facetName, event.target.value);
    }

    router.replace(`?${removeUnwantedQueryParams(params).toString()}`);
  }

  const virtualizer = useVirtualizer({
    gap: 4,
    count: values.length,
    estimateSize: () => 20,
    getScrollElement: () => ref.current,
  });

  return (
    <div ref={ref} className="scroll-shadow max-h-60 overflow-y-auto pb-4">
      <ul
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = values[virtualItem.index];

          if (!item) {
            return null;
          }

          return (
            <label
              key={virtualItem.key}
              className="absolute top-0 left-0 grid h-5 w-full grid-cols-[auto_1fr] items-center gap-x-2 text-sm whitespace-nowrap"
              style={{
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <input
                type="checkbox"
                name={name}
                value={item.id}
                onChange={onChange}
                checked={Boolean(
                  new URLSearchParams(searchParams).has(facetName, item.id),
                )}
                className="accent-black"
              />
              {item.label} ({item.count})
            </label>
          );
        })}
      </ul>
    </div>
  );
}
